import { FC, useState } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useMutation } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { UserEditRequestFormProps, UserEditRequestFormInput } from './types';
import { useStyles } from './styles';
import { useForm, Controller } from 'react-hook-form';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';
import CancelIcon from '@material-ui/icons/Cancel';
import {
  TextField,
  Button,
  Grid,
  Modal,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
} from '@material-ui/core';
import { uid } from 'utils';

export const UserEditRequestForm: FC<UserEditRequestFormProps> = ({
  open,
  refetch,
  handleClose,
  id,
  setId,
  defaultValue,
  label,
  payload,
}) => {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [editedValues, setEditedValues] = useState<any>({});

  const {
    userEditRequestTableStore: { updateUserFormEditRequest },
    notificationStore: { openNotification },
  } = useStore();

  const updateUserEditRequestReq = useMutation(
    (data: UserEditRequestFormInput) => updateUserFormEditRequest(id, data),
    {
      mutationKey: 'updateUserEditRequestReq',
      onSuccess: () => {
        openNotification('success', `Successfully updated`);
        refetch();
        reset();
        handleClose();
      },
      onError: () => openNotification('error', `Can't update user`),
    }
  );

  useAppPreloader(updateUserEditRequestReq.isLoading);

  const { control, handleSubmit, reset, setValue, getValues } = useForm<any>();

  const handleEdit = () => {
    setIsEditing(true);
    // Store current values when starting edit
    const currentValues = getValues();
    setEditedValues(currentValues);
  };

  const handleSaveEdit = () => {
    const currentValues = getValues();
    setEditedValues(currentValues);
    setIsEditing(false);
    openNotification('success', 'Changes saved locally');
  };

  const handleCancelEdit = () => {
    // Restore previous values
    Object.keys(editedValues).forEach((key) => {
      setValue(key, editedValues[key]);
    });
    setIsEditing(false);
  };

  const onSubmit = (data: any) => {
    let finalPayload;
    if (Array.isArray(payload?.customAttribute) && payload.customAttribute.length > 0) {
      finalPayload = { customAttribute: data || [] };
    } else {
      finalPayload = { ...data };
    }
    updateUserEditRequestReq.mutate(finalPayload);
  };

  return (
    <Modal className={classes.modal} disablePortal disableEnforceFocus disableAutoFocus open={open}>
      <Paper className={classes.paper} elevation={3}>
        <Grid container justify='space-between' alignItems='center'>
          <p className={classes.label}>User Requests</p>
          <div>
            {!isEditing ? (
              <IconButton onClick={handleEdit} title='Edit'>
                <EditIcon color='primary' />
              </IconButton>
            ) : (
              <>
                <IconButton onClick={handleSaveEdit} title='Save Changes'>
                  <SaveIcon style={{ color: 'green' }} />
                </IconButton>
                <IconButton onClick={handleCancelEdit} title='Cancel'>
                  <CancelIcon style={{ color: 'orange' }} />
                </IconButton>
              </>
            )}
            <IconButton
              style={{ marginRight: -16 }}
              onClick={() => {
                reset();
                setId(null);
                setIsEditing(false);
                handleClose();
              }}
            >
              <CloseIcon color='primary' />
            </IconButton>
          </div>
        </Grid>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid key={uid()} container justify='space-between'>
            {payload?.customAttribute.length > 0 ? (
              payload?.customAttribute.map((item: any) => (
                <Controller
                  key={item.label}
                  name={item.label ? item.label : ''}
                  control={control}
                  defaultValue={item.newValue || ''}
                  render={({ field, fieldState: { invalid } }) =>
                    item.options && item.options.length > 0 ? (
                      <FormControl className={classes.textField} error={invalid}>
                        <InputLabel>{item.label}</InputLabel>
                        <Select {...field} label={item.label} disabled={!isEditing}>
                          {item.options.map((option: string) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        {invalid && <FormHelperText>Required field</FormHelperText>}
                      </FormControl>
                    ) : (
                      <TextField
                        {...field}
                        className={classes.textField}
                        label={item.label}
                        error={invalid}
                        autoComplete='fname'
                        helperText={invalid ? 'Required field' : ''}
                        disabled={!isEditing}
                        variant={isEditing ? 'outlined' : 'filled'}
                      />
                    )
                  }
                />
              ))
            ) : (
              <Controller
                name={label ? label : ''}
                render={({ field, fieldState: { invalid } }) => (
                  <TextField
                    {...field}
                    className={classes.textField}
                    label={label}
                    rows={6}
                    error={invalid}
                    autoComplete='fname'
                    helperText={invalid ? 'Required field' : ''}
                    disabled={!isEditing}
                    variant={isEditing ? 'outlined' : 'filled'}
                    multiline
                  />
                )}
                defaultValue={defaultValue}
                control={control}
              />
            )}
          </Grid>
          {isEditing && (
            <Typography variant='body2' style={{ color: 'orange', marginBottom: '10px' }}>
              Edit mode: Make your changes and click Save to apply them locally, or Cancel to discard.
            </Typography>
          )}
          <Button className={classes.btn} type='submit' fullWidth variant='contained' color='primary'>
            Update Request (API Call)
          </Button>
        </form>
      </Paper>
    </Modal>
  );
};
